import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { comboboxCreateQuery } from './combobox-create';

export type ComboboxOption = {
  label: string;
  value: string;
  /** Options with a group show under its heading, in the order the groups first appear. */
  group?: string;
  /** Shown but can't be picked (the value another field already holds). */
  disabled?: boolean;
};

type Option = ComboboxOption;

/** Options split into their groups, in order; ungrouped options form a group with no heading. */
function optionSections(options: readonly Option[]): Array<{ heading?: string; options: Option[] }> {
  const sections: Array<{ heading?: string; options: Option[] }> = [];
  for (const option of options) {
    const section = sections.find((s) => s.heading === option.group);
    if (section) section.options.push(option);
    else sections.push({ heading: option.group, options: [option] });
  }
  return sections;
}

type Props = {
  id: string;
  /** When omitted, the caller is expected to render its own label. */
  label?: string;
  labelClassName?: string;
  value: string;
  displayValue: string;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  /**
   * sm (32, small text) for dense rows and table cells; toolbar (32, normal text) beside a
   * `Select` of size sm, as in chart settings; default (36) in forms.
   */
  size?: 'default' | 'sm' | 'toolbar';
  onSelect: (value: string) => void;
  onInputChange?: (value: string) => void;
  /** Show a Create item when the query does not match an existing option. */
  allowCreate?: boolean;
};

function isOpenKey(key: string): boolean {
  return key === 'ArrowDown' || key === 'Enter' || key === ' ';
}

function isTypeaheadKey(e: KeyboardEvent): boolean {
  return (
    e.key.length === 1 &&
    e.key !== ' ' &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey
  );
}

function focusSearchInput(input: HTMLInputElement | null) {
  if (!input) return;
  input.focus();
  const len = input.value.length;
  input.setSelectionRange(len, len);
}

/**
 * Searchable single-select combobox (Command + Popover). Options can have group headings and be
 * disabled, so a long menu (every flow) can be searched by typing.
 */
export function ComboboxField({
  id,
  label,
  labelClassName,
  value,
  displayValue,
  options,
  placeholder,
  searchPlaceholder,
  emptyText = 'No results.',
  icon,
  disabled,
  className,
  size = 'default',
  onSelect,
  onInputChange,
  allowCreate = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = (onInputChange ? displayValue : query).trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, query, displayValue, onInputChange]);

  const currentSearch = onInputChange ? displayValue : query;

  const createQuery = comboboxCreateQuery(allowCreate, currentSearch, options);

  const setSearch = (next: string) => {
    if (onInputChange) onInputChange(next);
    else setQuery(next);
  };

  const openList = (initialQuery?: string) => {
    if (disabled) return;
    if (initialQuery != null) setSearch(initialQuery);
    setOpen(true);
  };

  // After open, move focus into the search field so typing can continue.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() =>
      focusSearchInput(searchInputRef.current)
    );
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const control = (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        // Closing drops the search, the caller's too, so the trigger goes back to its value.
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? `${id}-listbox` : undefined}
          disabled={disabled}
          size={size === 'default' ? 'default' : 'sm'}
          className={cn(
            // Override button `inline-flex shrink-0` so long labels truncate in grids
            'w-full min-w-0 max-w-full shrink justify-between overflow-hidden font-normal',
            size === 'sm' && 'text-xs',
            className
          )}
          onKeyDown={(e) => {
            if (disabled) return;

            // If the list is open but focus is still on the trigger, forward
            // typeahead keys into the search field.
            if (open) {
              if (isTypeaheadKey(e)) {
                e.preventDefault();
                setSearch(currentSearch + e.key);
                focusSearchInput(searchInputRef.current);
              }
              return;
            }

            if (isOpenKey(e.key)) {
              e.preventDefault();
              openList();
              return;
            }
            if (isTypeaheadKey(e)) {
              e.preventDefault();
              openList(e.key);
            }
          }}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {icon ? (
              <span className="inline-flex max-w-full items-center gap-2">
                {icon}
                <span className="truncate">
                  {displayValue || placeholder || 'Select…'}
                </span>
              </span>
            ) : (
              displayValue || placeholder || 'Select…'
            )}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[max(var(--radix-popover-trigger-width),min(28rem,calc(100vw-2rem)))] p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Focus the search input ourselves via ref (see useEffect).
          e.preventDefault();
          focusSearchInput(searchInputRef.current);
        }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            ref={searchInputRef}
            placeholder={searchPlaceholder ?? placeholder}
            value={currentSearch}
            onValueChange={(v) => setSearch(v)}
          />
            <CommandList id={`${id}-listbox`}>
            {filtered.length === 0 && !createQuery ? (
              <CommandEmpty>{emptyText}</CommandEmpty>
            ) : null}
            {createQuery ? (
              <CommandGroup>
                <CommandItem
                  value={`__create__:${createQuery}`}
                  onSelect={() => {
                    onSelect(createQuery);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Plus className="mr-2 size-4 shrink-0 opacity-70" />
                  <span className="min-w-0 whitespace-normal">
                    Create “{createQuery}”
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {optionSections(filtered).map((section, index) => (
              <Fragment key={section.heading ?? `__section_${index}`}>
                {index > 0 ? <CommandSeparator /> : null}
                <CommandGroup heading={section.heading}>
                  {section.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      onSelect={() => {
                        onSelect(option.value);
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4 shrink-0',
                          value === option.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="min-w-0 whitespace-normal">{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Fragment>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (!label) {
    return <div className="min-w-0 w-full max-w-full">{control}</div>;
  }

  return (
    <div className="grid min-w-0 w-full max-w-full gap-2">
      <Label htmlFor={id} className={labelClassName}>
        {label}
      </Label>
      {control}
    </div>
  );
}
